import { supabase } from '@/services/supabase/client';
import type {
  EntityType,
  EntityRelationship,
  GraphNode,
  RelationshipHealth,
  ConfidenceSource,
  RelationshipSignal,
} from './types';

export type { EntityType, EntityRelationship, GraphNode, RelationshipHealth, RelationshipSignal } from './types';

export interface ResolvedEntity {
  type: EntityType;
  id: string;
  slug: string;
  title: string;
}

const ENTITY_TABLE: Partial<
  Record<EntityType, { table: string; titleCol: 'title' | 'name'; published?: boolean; active?: boolean }>
> = {
  knowledge: { table: 'knowledge_objects', titleCol: 'title', published: true },
  industry: { table: 'industries', titleCol: 'name', active: true },
  goal: { table: 'business_goals', titleCol: 'title', active: true },
  problem: { table: 'business_problems', titleCol: 'title', published: true },
  workflow: { table: 'workflows', titleCol: 'title', published: true },
  product: { table: 'products', titleCol: 'title', published: true },
  capability: { table: 'capabilities', titleCol: 'name', active: true },
  ai_capability: { table: 'ai_capabilities', titleCol: 'name', active: true },
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export async function resolveEntity(
  entityType: EntityType,
  slugOrId: string
): Promise<ResolvedEntity | null> {
  const config = ENTITY_TABLE[entityType];
  if (!config || !slugOrId.trim()) return null;

  const trimmed = slugOrId.trim();
  let query = supabase
    .from(config.table)
    .select(`id, slug, ${config.titleCol}`)
    .limit(1);

  if (UUID_RE.test(trimmed)) {
    query = query.eq('id', trimmed);
  } else {
    query = query.eq('slug', trimmed);
  }

  if (config.published) query = query.eq('status', 'published');
  if (config.active) query = query.eq('is_active', true);

  const { data, error } = await query.maybeSingle();
  if (error || !data) return null;

  const row = data as Record<string, unknown>;
  const id = String(row.id ?? '');
  const slug = String(row.slug ?? '');
  const title = String(row[config.titleCol] ?? slug);
  if (!id) return null;

  return {
    type: entityType,
    id,
    slug,
    title,
  };
}

export async function resolveEntityTitles(
  refs: Array<{ type: EntityType; id: string }>
): Promise<Map<string, string>> {
  const titles = new Map<string, string>();
  const byType = new Map<EntityType, string[]>();

  for (const ref of refs) {
    const list = byType.get(ref.type) ?? [];
    list.push(ref.id);
    byType.set(ref.type, list);
  }

  await Promise.all(
    Array.from(byType.entries()).map(async ([type, ids]) => {
      const config = ENTITY_TABLE[type];
      if (!config || ids.length === 0) return;
      const unique = [...new Set(ids)];
      const { data } = await supabase
        .from(config.table)
        .select(`id, ${config.titleCol}`)
        .in('id', unique);
      for (const row of data ?? []) {
        const r = row as Record<string, unknown>;
        const id = String(r.id ?? '');
        if (!id) continue;
        const label = String(r[config.titleCol] ?? id.slice(0, 8));
        titles.set(`${type}:${id}`, label);
      }
    })
  );

  return titles;
}

export async function getFeaturedGraphEntities(
  limit = 6
): Promise<ResolvedEntity[]> {
  const results: ResolvedEntity[] = [];

  const { data: knowledge } = await supabase
    .from('knowledge_objects')
    .select('id, slug, title')
    .eq('status', 'published')
    .order('updated_at', { ascending: false })
    .limit(Math.ceil(limit / 2));

  for (const row of knowledge ?? []) {
    results.push({
      type: 'knowledge',
      id: row.id,
      slug: row.slug,
      title: row.title,
    });
  }

  const { data: industries } = await supabase
    .from('industries')
    .select('id, slug, name')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .limit(Math.floor(limit / 2));

  for (const row of industries ?? []) {
    results.push({
      type: 'industry',
      id: row.id,
      slug: row.slug,
      title: row.name,
    });
  }

  return results.slice(0, limit);
}

export interface GetRelationshipsOptions {
  direction?: 'both' | 'outbound' | 'inbound';
  relationshipType?: string;
  targetType?: EntityType;
  limit?: number;
}

export async function getEntityRelationships(
  entityType: EntityType,
  entityId: string,
  options: GetRelationshipsOptions = {}
): Promise<EntityRelationship[]> {
  const { data, error } = await supabase.rpc('get_entity_relationships', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_direction: options.direction || 'both',
    p_relationship_type: options.relationshipType || null,
    p_target_type: options.targetType || null,
    p_limit: options.limit || 20,
  });

  if (error) {
    console.error('Failed to get entity relationships:', error);
    return [];
  }

  return (data ?? []) as EntityRelationship[];
}

export interface TraverseOptions {
  maxDepth?: number;
  filterTargetTypes?: EntityType[];
  minWeight?: number;
  limit?: number;
}

export async function traverseGraph(
  startType: EntityType,
  startId: string,
  options: TraverseOptions = {}
): Promise<GraphNode[]> {
  const { data, error } = await supabase.rpc('traverse_graph', {
    p_start_type: startType,
    p_start_id: startId,
    p_max_depth: options.maxDepth || 2,
    p_filter_target_types: options.filterTargetTypes || null,
    p_min_weight: options.minWeight || 0.3,
    p_limit: options.limit || 30,
  });

  if (error) {
    console.error('Failed to traverse graph:', error);
    return [];
  }

  return (data ?? []) as GraphNode[];
}

export async function getEntityHealthScore(
  entityType: EntityType,
  entityId: string
): Promise<RelationshipHealth | null> {
  const { data, error } = await supabase.rpc('compute_relationship_health', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });

  if (error) {
    console.error('Failed to compute health:', error);
    return null;
  }

  const row = Array.isArray(data) ? data[0] : data;
  return row as RelationshipHealth | null;
}

export async function getRecommendationPath(problemId: string): Promise<GraphNode[]> {
  return traverseGraph('problem', problemId, {
    maxDepth: 3,
    filterTargetTypes: ['capability', 'workflow', 'knowledge', 'product'],
    minWeight: 0.3,
    limit: 15,
  });
}

export async function createRelationship(
  sourceType: EntityType,
  sourceId: string,
  targetType: EntityType,
  targetId: string,
  relationshipTypeSlug: string,
  weight: number = 0.5,
  confidenceSource: ConfidenceSource = 'manual'
): Promise<boolean> {
  const { data: relType } = await supabase
    .from('relationship_types')
    .select('id')
    .eq('slug', relationshipTypeSlug)
    .single();

  if (!relType) return false;

  const { error } = await supabase.from('entity_relationships').upsert(
    {
      source_entity_type: sourceType,
      source_entity_id: sourceId,
      target_entity_type: targetType,
      target_entity_id: targetId,
      relationship_type_id: relType.id,
      weight,
      confidence_source: confidenceSource,
    },
    {
      onConflict: 'source_entity_type,source_entity_id,target_entity_type,target_entity_id,relationship_type_id',
    }
  );

  return !error;
}

export async function recordSignal(signal: RelationshipSignal): Promise<void> {
  await supabase.from('relationship_signals').insert({
    source_entity_type: signal.source.type,
    source_entity_id: signal.source.id,
    target_entity_type: signal.target.type,
    target_entity_id: signal.target.id,
    signal_type: signal.signalType,
    session_id: signal.sessionId || null,
  });
}

export async function getSimilarEntities(
  entityType: EntityType,
  entityId: string,
  limit: number = 6
): Promise<GraphNode[]> {
  const relationships = await getEntityRelationships(entityType, entityId, {
    direction: 'both',
    limit,
  });

  return relationships.map((rel) => ({
    entity_type: rel.direction === 'outbound' ? rel.target_entity_type : rel.source_entity_type,
    entity_id: rel.direction === 'outbound' ? rel.target_entity_id : rel.source_entity_id,
    depth: 1,
    path_weight: rel.weight,
    via_relationship: rel.relationship_type,
    path: [
      `${entityType}:${entityId}`,
      `${rel.direction === 'outbound' ? rel.target_entity_type : rel.source_entity_type}:${rel.direction === 'outbound' ? rel.target_entity_id : rel.source_entity_id}`,
    ],
  }));
}
